import React from 'react';
import { Circle, AlertTriangle, CheckCircle2 } from 'lucide-react';

const VehicleTireMap = ({ vehicle, onTireClick, onEmptyPositionClick }) => {
  // Mapear pneus por posição
  const getTireByPosition = (position) => {
    return vehicle.tires?.find(t => t.position === position);
  };

  // Determinar cor do pneu baseado em profundidade
  const getTireColor = (tire) => {
    if (!tire) return 'bg-gray-300 dark:bg-gray-600';

    const treadDepth = parseFloat(tire.current_tread_depth_mm) || 0;

    if (treadDepth >= 5) return 'bg-green-500'; // OK
    if (treadDepth >= 3) return 'bg-yellow-500'; // Atenção
    return 'bg-red-500'; // Crítico
  };

  // Determinar status visual
  const getTireStatus = (tire) => {
    if (!tire) return { icon: null, label: 'Vazio', color: 'text-gray-400' };

    const treadDepth = parseFloat(tire.current_tread_depth_mm) || 0;

    if (treadDepth >= 5) {
      return { icon: CheckCircle2, label: 'OK', color: 'text-green-500' };
    }
    if (treadDepth >= 3) {
      return { icon: AlertTriangle, label: 'Atenção', color: 'text-yellow-500' };
    }
    return { icon: AlertTriangle, label: 'Crítico', color: 'text-red-500' };
  };

  // Renderizar pneu individual
  const renderTire = (position, label, style = {}) => {
    const tire = getTireByPosition(position);
    const status = getTireStatus(tire);
    const StatusIcon = status.icon;

    return (
      <div
        key={position}
        className={`relative ${style.className || ''}`}
        style={style}
      >
        <button
          onClick={() => {
            if (tire) {
              onTireClick && onTireClick(tire, vehicle);
            } else {
              onEmptyPositionClick && onEmptyPositionClick(position, label);
            }
          }}
          className={`
            w-16 h-20 rounded-lg border-4 border-gray-800 dark:border-gray-200
            ${getTireColor(tire)}
            ${tire
              ? 'cursor-pointer hover:scale-110 hover:shadow-xl'
              : 'cursor-pointer hover:scale-105 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-lg'
            }
            transition-all duration-200
            flex flex-col items-center justify-center
            relative
          `}
          title={tire
            ? `${tire.brand} ${tire.model} - ${tire.serial_number}`
            : `Clique para instalar pneu na posição ${label}`
          }
        >
          {/* Ícone de status */}
          {StatusIcon && (
            <StatusIcon className="w-4 h-4 text-white mb-1" />
          )}

          {/* Ícone de adicionar para posições vazias */}
          {!tire && (
            <div className="text-white text-2xl font-bold mb-1">+</div>
          )}

          {/* Label da posição */}
          <span className="text-[10px] font-bold text-white bg-black bg-opacity-50 px-1 rounded">
            {label}
          </span>

          {/* Profundidade */}
          {tire && tire.current_tread_depth_mm && (
            <span className="text-[9px] font-semibold text-white bg-black bg-opacity-50 px-1 rounded mt-1">
              {parseFloat(tire.current_tread_depth_mm).toFixed(1)}mm
            </span>
          )}
        </button>
      </div>
    );
  };

  // Determinar layout baseado no tipo de veículo ou número de pneus
  const renderVehicleLayout = () => {
    const tiresCount = vehicle.tires?.length || 0;

    // Caminhão 6x2 (10 pneus)
    if (tiresCount >= 10 || vehicle.vehicle_type === 'truck') {
      return (
        <div className="relative w-full max-w-md mx-auto">
          {/* Corpo do caminhão (visual) */}
          <div className="relative bg-gradient-to-b from-gray-300 to-gray-400 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-8 shadow-2xl">

            {/* Frente do caminhão */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 w-32 h-4 bg-blue-500 dark:bg-blue-600 rounded-t-lg"></div>

            {/* Layout dos pneus */}
            <div className="space-y-8">
              {/* Eixo dianteiro (2 pneus) */}
              <div className="flex justify-between items-center">
                {renderTire('front_left', 'DE')}
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Dianteiro
                </div>
                {renderTire('front_right', 'DD')}
              </div>

              {/* Eixo intermediário (4 pneus - duplo) */}
              <div className="flex justify-between items-center">
                <div className="flex gap-1">
                  {renderTire('middle_outer_left', 'IEE')}
                  {renderTire('middle_inner_left', 'IIE')}
                </div>
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Intermediário
                </div>
                <div className="flex gap-1">
                  {renderTire('middle_inner_right', 'IID')}
                  {renderTire('middle_outer_right', 'IED')}
                </div>
              </div>

              {/* Eixo traseiro (4 pneus - duplo) */}
              <div className="flex justify-between items-center">
                <div className="flex gap-1">
                  {renderTire('rear_outer_left', 'TEE')}
                  {renderTire('rear_inner_left', 'TIE')}
                </div>
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Traseiro
                </div>
                <div className="flex gap-1">
                  {renderTire('rear_inner_right', 'TID')}
                  {renderTire('rear_outer_right', 'TED')}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Carro/Van (4-6 pneus)
    if (tiresCount >= 4) {
      return (
        <div className="relative w-full max-w-sm mx-auto">
          <div className="relative bg-gradient-to-b from-gray-300 to-gray-400 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-6 shadow-2xl">

            {/* Frente do veículo */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 w-24 h-3 bg-blue-500 dark:bg-blue-600 rounded-t-lg"></div>

            <div className="space-y-12">
              {/* Dianteiro */}
              <div className="flex justify-between items-center">
                {renderTire('front_left', 'DE')}
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Dianteiro
                </div>
                {renderTire('front_right', 'DD')}
              </div>

              {/* Traseiro */}
              <div className="flex justify-between items-center">
                {renderTire('rear_left', 'TE')}
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Traseiro
                </div>
                {renderTire('rear_right', 'TD')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Sem pneus ou configuração desconhecida
    return (
      <div className="text-center py-12">
        <Circle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          Nenhum pneu instalado neste veículo
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Cabeçalho do veículo */}
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          {vehicle.brand} {vehicle.model}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Placa: {vehicle.license_plate}
        </p>
      </div>

      {/* Diagrama do veículo */}
      {renderVehicleLayout()}

      {/* Legenda */}
      <div className="flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-gray-700 dark:text-gray-300">OK (≥5mm)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500"></div>
          <span className="text-gray-700 dark:text-gray-300">Atenção (3-5mm)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500"></div>
          <span className="text-gray-700 dark:text-gray-300">Crítico (&lt;3mm)</span>
        </div>
      </div>
    </div>
  );
};

export default VehicleTireMap;
